import {
    Box,
    CircularProgress,
    LinearProgress,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ImsProgressProps } from "../../@types/ContextProviderRelated";
import {
    EElementDirection,
    EProgressType,
    EProgressVariant
} from "../../@types/Enums";
import "../../assets/styles/animations.css";
import { immersightBlue } from "../../utils/colors";
import { normalise } from "../../utils/numberUtils";

/**
 * Generates a progress bar or a progress spinner to display progress to the user.
 *
 * @param {ImsProgressProps} props the props object for the ims progress component.
 * @returns {JSX.Element} ImsProgress object.
 */
const ImsProgress = (props: ImsProgressProps): JSX.Element => {
  const {
    variant = EProgressVariant.indeterminate,
    direction = EElementDirection.LEFT_TO_RIGHT,
    type = EProgressType.circular,
    min = 0,
    max = 100,
    value = 0,
    showLabel = true,
    labelUnit = "%",
    progressColor = `${immersightBlue} !important`,
    labelColor = "black !important",
    backgroundColor = "transparent !important",
    style = {},
  } = props;
  const progressVariant =
    variant === EProgressVariant.determinate ? "determinate" : "indeterminate";
  const [actualValue, setActualValue] = useState<number>(
    normalise(value, min, max)
  );
  // UseEffect hook to normalise the actual value of this progress component after changes have been detected.
  useEffect(() => {
    // Function that checks if the actual value is already greater than the update. Prevents it if it is.
    const updateActualValue = (newValue: number): void => {
      if (newValue >= actualValue) {
        setActualValue(newValue);
      }
    };

    updateActualValue(normalise(value, min, max));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);

  /**
   * Function to parse the direction param of this component and translate it into a usable transform css statement.
   *
   * @returns {string} the transform action as a string.
   */
  const parseDirectionToTransformString = (): string => {
    switch (direction) {
      default:
      case EElementDirection.LEFT_TO_RIGHT:
        return "none";
      case EElementDirection.TOP_TO_BOTTOM:
        return "rotate(90deg)";
      case EElementDirection.RIGHT_TO_LEFT:
        return "scale(-1, -1)";
      case EElementDirection.BOTTOM_TO_TOP:
        return "none";
    }
  };

  return (
    <>
      {type === EProgressType.circular ? (
        <Box
          component="div"
          sx={{
            position: "relative",
            display: "inline-flex",
            justifyContent: "space-between",
            ...style,
          }}
        >
          <CircularProgress
            className={actualValue < max ? "pulseProgress" : "solidProgress"}
            variant={
              Number.isNaN(Math.round(actualValue))
                ? "indeterminate"
                : progressVariant
            }
            value={actualValue}
            sx={{
              color: progressColor,
            }}
          />
          {showLabel &&
            !Number.isNaN(Math.round(actualValue)) &&
            actualValue !== max && (
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: labelColor }}
                >
                  {`${Math.round(actualValue)}${labelUnit}`}
                </Typography>
              </Box>
            )}
        </Box>
      ) : (
        <Box
          component="div"
          sx={{
            position: "relative",
            display: "inline-flex",
            flexDirection: "column",
            ...style,
          }}
        >
          <LinearProgress
            className={actualValue < max ? "pulseProgress" : "solidProgress"}
            variant={
              Number.isNaN(Math.round(value))
                ? "indeterminate"
                : progressVariant
            }
            value={actualValue}
            sx={{
              color: progressColor,
              transform: parseDirectionToTransformString(),
              "& .MuiLinearProgress-bar": {
                backgroundColor: backgroundColor,
                width: "100%",
              },
              width: "100%",
            }}
          />
          {showLabel &&
            !Number.isNaN(Math.round(actualValue)) &&
            actualValue !== max && (
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: labelColor }}
                >
                  {`${Math.round(actualValue)}${labelUnit}`}
                </Typography>
              </Box>
            )}
        </Box>
      )}
    </>
  );
};

export default ImsProgress;
